/**
 * What a volunteer sees.
 *
 * The whole of their portal: the work they were asked to do, and a button on
 * the pieces that are waiting on them. Not the directory, not other people's
 * hours, not the programs they are not part of — the API refuses all of that
 * (D21), and a screen offering links into it would just be a tour of things
 * that answer 403.
 *
 * The page is organised by **what needs you**, not by date or by program. A
 * volunteer opens this to answer one question — is there anything for me to do?
 * — so the sessions awaiting them come first under their own heading, and
 * everything else is history below.
 */

import { useState } from 'react';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from '../../components/common';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../hooks/useAuth';
import { useConfirmSession, useMySessions } from '../../hooks/useSessions';
import type { Session } from '../../services/api/sessions';
import { formatDate } from '../../utils/format';
import styles from './MySessionsPage.module.css';

/** A range reads as a range; a one-day session reads as a day. */
function whenText(session: Session): string {
  if (!session.end_date || session.end_date === session.start_date) return formatDate(session.start_date);
  return `${formatDate(session.start_date)} – ${formatDate(session.end_date)}`;
}

/**
 * Is this volunteer the one being waited on?
 *
 * Only then is a button honest. A session awaiting Parinaam is theirs to finish,
 * and offering a volunteer a button that would answer 403 is worse than showing
 * them nothing.
 */
function volunteerMayConfirm(session: Session): boolean {
  if (session.status === 'COMPLETED') return false;
  if (session.responsibility === 'PARINAAM') return false;
  return session.awaiting === 'VOLUNTEER' || session.awaiting === 'BOTH';
}

function SessionRow({ session }: { session: Session }) {
  const confirm = useConfirmSession();
  const toast = useToast();
  const [asking, setAsking] = useState(false);

  const mine = volunteerMayConfirm(session);

  const doConfirm = () => {
    confirm.mutate(session.session_id, {
      onSuccess: (result) => {
        setAsking(false);
        toast.success(
          result.completed
            ? 'Thank you — that session is now complete.'
            : 'Thank you. Parinaam still has to confirm their side.'
        );
      },
      onError: (error) => toast.error(error, 'Could not record your confirmation.'),
    });
  };

  return (
    <li className={styles.row} data-status={session.status}>
      <div className={styles.main}>
        <p className={styles.name}>{session.session_name}</p>
        <p className={styles.meta}>
          {session.activity_name} · {session.program_name}
        </p>
        <p className={styles.meta}>
          {whenText(session)}
        </p>
      </div>

      <div className={styles.side}>
        <StatusBadge status={session.status} />

        {session.status === 'COMPLETED' ? (
          <span className={styles.done}>Complete</span>
        ) : mine ? (
          asking ? (
            <div className={styles.confirmBox}>
              {/*
                A confirmation is a statement of fact about work somebody did,
                and it cannot be taken back by the person who made it — only an
                admin can reopen a session. So it asks once.
              */}
              <p className={styles.confirmAsk}>
                Confirm that your part of this session is finished?
              </p>
              <div className={styles.confirmActions}>
                <Button
                  variant="primary"
                  onClick={doConfirm}
                  disabled={confirm.isPending}
                >
                  {confirm.isPending ? 'Confirming…' : 'Yes, it is done'}
                </Button>
                <Button onClick={() => setAsking(false)} disabled={confirm.isPending}>
                  Not yet
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="primary" onClick={() => setAsking(true)}>
              Mark as done
            </Button>
          )
        ) : (
          <span className={styles.waiting}>Waiting on Parinaam</span>
        )}
      </div>
    </li>
  );
}

export function MySessionsPage() {
  const { user } = useAuth();
  const sessions = useMySessions();

  if (sessions.isPending) return <LoadingState label="Loading your activities…" />;
  if (sessions.isError) {
    return (
      <>
        <PageHeader title="Your volunteering" />
        <Card>
          <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
        </Card>
      </>
    );
  }

  const rows = sessions.data ?? [];
  const waiting = rows.filter(volunteerMayConfirm);
  const rest = rows.filter((session) => !volunteerMayConfirm(session));

  return (
    <>
      <PageHeader
        title="Your volunteering"
        description={user ? `Signed in as ${user.display_name}` : undefined}
      />

      {rows.length === 0 ? (
        <Card>
          <EmptyState
            title="Nothing assigned yet"
            message="Activities you join will appear here."
          />
        </Card>
      ) : (
        <>
          {waiting.length > 0 && (
            <Card title={`Waiting for you (${waiting.length})`}>
              <ul className={styles.list}>
                {waiting.map((session) => (
                  <SessionRow key={session.session_id} session={session} />
                ))}
              </ul>
            </Card>
          )}

          <Card title={waiting.length > 0 ? 'Everything else' : 'Your activities'}>
            {rest.length === 0 ? (
              <p className={styles.allDone}>Nothing else — you are up to date.</p>
            ) : (
              <ul className={styles.list}>
                {rest.map((session) => (
                  <SessionRow key={session.session_id} session={session} />
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </>
  );
}
